/**
 * Deterministic dedupe key for bank transactions.
 *
 * The problem ANZ creates for us: no transaction ID is exported in the
 * CSV. So the same monthly statement re-uploaded next month overlaps
 * with whatever's already in Firestore. We need a key that:
 *
 *   - Is stable across re-uploads (same row → same id)
 *   - Doesn't collide false-positively when two GENUINELY DIFFERENT
 *     transactions happen on the same day for the same amount at the
 *     same merchant (this happens — your sample data has two
 *     'EFTPOS GLYNDE MITRE 10 -$1.00' on the same day)
 *
 * Strategy: hash the natural key (account + date + amount + desc) but
 * include an "Nth-of-K" suffix that's computed per-batch. Within a
 * given upload, rows are grouped by the natural key and numbered. The
 * commit pass reads the existing-doc count for each group and inserts
 * only the missing ones.
 *
 * Concretely:
 *   - File has 2 identical $1 Mitre 10 lines on 2026-05-22.
 *     Hashes: H1 = sha256("...|1"), H2 = sha256("...|2"). Both insert.
 *   - Re-upload the same file next month.
 *     We compute H1 + H2 again, both already exist → skip both. ✓
 *   - User goes back to the merchant the same week and gets billed
 *     a third $1.
 *     Next month's CSV has 3 identical lines. We compute H1, H2, H3 —
 *     H1 + H2 exist, H3 doesn't, so only H3 inserts. ✓
 *
 * Edge case: a customer correction reverses one of two identical
 * lines mid-month, dropping the count from 2 to 1. The first time we
 * re-import we'll see only one row in the new file but two in
 * Firestore, and we won't delete the orphan. The user can manually
 * resolve via the Undo-this-import button on the original batch. This
 * is rare enough to live with.
 */

import { sha256Hex } from './anzCsvParser';

/** The natural key fields. Kept as an object so the call site is
 *  self-documenting and we can extend without breaking existing hashes
 *  (we wouldn't — that would re-key every doc — but the structure
 *  makes the intent obvious). */
export interface NaturalKey {
  accountId: string;
  date: string;          // YYYY-MM-DD
  amount: number;        // signed
  descriptionRaw: string;
}

/** Build the string that goes into sha256. Amount is forced to fixed
 *  2dp so 199 vs 199.00 don't differ. */
function naturalKeyString(k: NaturalKey, occurrence: number): string {
  return [
    k.accountId,
    k.date,
    k.amount.toFixed(2),
    k.descriptionRaw,
    `${occurrence}`, // N-of-K suffix
  ].join('|');
}

/** Compute the deterministic id for the Nth occurrence (1-indexed)
 *  of an identical natural-key group. */
export async function hashTransactionKey(k: NaturalKey, occurrence: number): Promise<string> {
  return sha256Hex(naturalKeyString(k, occurrence));
}

/** Compute hashes for every row in a file's worth of rows, accounting
 *  for in-file duplicates via N-of-K numbering. Returns the hashes in
 *  the same order as the input rows. */
export async function hashRowsWithOccurrenceGrouping(
  accountId: string,
  rows: Array<{ date: string; amount: number; description: string }>,
): Promise<string[]> {
  // First pass: count occurrences as we go to assign each row its
  // 1-indexed position within its (date, amount, desc) group.
  const counters = new Map<string, number>();
  const occurrences: number[] = new Array(rows.length);
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const groupKey = `${r.date}|${r.amount.toFixed(2)}|${r.description}`;
    const next = (counters.get(groupKey) || 0) + 1;
    counters.set(groupKey, next);
    occurrences[i] = next;
  }
  // Second pass: hash each row with its occurrence number.
  const hashes: string[] = new Array(rows.length);
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    hashes[i] = await hashTransactionKey({
      accountId,
      date: r.date,
      amount: r.amount,
      descriptionRaw: r.description,
    }, occurrences[i]);
  }
  return hashes;
}
